import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import api, { humanError } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";

/**
 * Single sign-on configuration.
 *
 * Enterprise-only, so this renders a locked state rather than hiding: a buyer
 * evaluating the plan should be able to see what they would get, and an admin
 * who has it should not have to guess where it lives.
 *
 * The client secret is never read back from the server -- only whether one is
 * set -- so the field is left blank on an existing configuration and only sent
 * when someone actually types a new one.
 */
export default function SsoSettings() {
  const { role } = useAuth();
  const canAdmin = can(role, "tenant.settings");

  const [config, setConfig] = useState(undefined);
  const [locked, setLocked] = useState(false);
  const [form, setForm] = useState({ issuer: "", client_id: "", client_secret: "", email_domains: "" });
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/sso/config")
      .then(({ data }) => {
        setConfig(data.config);
        if (data.config) {
          setForm({
            issuer: data.config.issuer || "",
            client_id: data.config.client_id || "",
            client_secret: "",
            email_domains: (data.config.email_domains || []).join(", "),
          });
        }
      })
      .catch(() => setConfig(null));

  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/sso/config", {
        issuer: form.issuer.trim(),
        client_id: form.client_id.trim(),
        client_secret: form.client_secret,
        email_domains: form.email_domains.split(",").map((d) => d.trim()).filter(Boolean),
        enabled: true,
      });
      toast.success("Single sign-on is configured");
      // The redirect URI has to be registered with the provider, and it is the
      // thing most often got wrong, so it is shown rather than assumed.
      toast.info(`Register this redirect URI with your provider: ${data.redirect_uri}`, { duration: 12000 });
      setForm((f) => ({ ...f, client_secret: "" }));
      load();
    } catch (e) {
      if (e?.response?.status === 402) setLocked(true);
      toast.error(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm("Turn off single sign-on? People will sign in with a password again.")) return;
    try {
      await api.delete("/sso/config");
      toast.success("Single sign-on removed");
      setConfig(null);
      setForm({ issuer: "", client_id: "", client_secret: "", email_domains: "" });
    } catch (e) {
      toast.error(humanError(e));
    }
  }

  if (config === undefined) return null;
  if (!canAdmin) return null;

  return (
    <section className="rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="sso-settings">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#0B2B33]">
          <KeyRound className="h-4 w-4 text-[#1F5FD8]" /> Single sign-on
        </div>
        {config ? (
          <span className="hs-chip bg-[#E8FBF1] text-[#16A46B]">Configured</span>
        ) : (
          <span className="hs-chip bg-[#F6F8FA] text-[#8A97A0]">Not set up</span>
        )}
      </div>
      <p className="mb-4 text-sm text-[#43535E]">
        Let your team sign in through your own identity provider. Works with anything that
        publishes an OpenID Connect discovery document — Okta, Entra ID, Google Workspace, Auth0.
      </p>

      {locked ? (
        <p className="rounded-lg bg-[#FFF4E2] px-4 py-3 text-sm text-[#43535E]">
          Single sign-on is available on Enterprise. Talk to us about moving plan.
        </p>
      ) : null}

      <form onSubmit={save} className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]" htmlFor="sso-issuer">
            Issuer URL
          </label>
          <input id="sso-issuer" required value={form.issuer} data-testid="sso-issuer"
            onChange={(e) => setForm({ ...form, issuer: e.target.value })}
            placeholder="https://your-company.okta.com"
            className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" />
          <p className="mt-1 text-xs text-[#8A97A0]">
            We read the provider's configuration from this address before saving, so a typo is
            caught here rather than the next time someone tries to sign in.
          </p>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]" htmlFor="sso-client">
              Client ID
            </label>
            <input id="sso-client" required value={form.client_id} data-testid="sso-client-id"
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]" htmlFor="sso-secret">
              Client secret
            </label>
            <input id="sso-secret" type="password" value={form.client_secret} data-testid="sso-client-secret"
              required={!config}
              onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
              placeholder={config ? "Leave blank to keep the current one" : ""}
              className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]" htmlFor="sso-domains">
            Email domains
          </label>
          <input id="sso-domains" required value={form.email_domains} data-testid="sso-domains"
            onChange={(e) => setForm({ ...form, email_domains: e.target.value })}
            placeholder="yourcompany.com, yourcompany.co.uk"
            className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" />
          <p className="mt-1 text-xs text-[#8A97A0]">
            Anyone signing in with an address at these domains is sent to your provider. A domain
            can belong to only one workspace.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <button type="submit" disabled={busy} data-testid="sso-save" className="hs-btn-primary text-sm disabled:opacity-60">
            {busy ? "Checking your provider…" : config ? "Update" : "Set up single sign-on"}
          </button>
          {config ? (
            <button type="button" onClick={remove} data-testid="sso-remove"
              className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm font-semibold text-[#B02328] hover:bg-[#FDECEC]">
              Turn off
            </button>
          ) : null}
        </div>
      </form>
    </section>
  );
}
